<script setup>
import Icon from 'next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { defineProps, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { currencyFormatter } from 'next/message/bubbles/TiktokShop/helper/formatter.js';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  currentChat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const toggleExpandOrderIds = ref({});
const store = useStore();

function toggle(orderId) {
  toggleExpandOrderIds.value[orderId] = !toggleExpandOrderIds.value[orderId];
}

const statusText = computed(() => {
  const status = props.order.status || '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
});

const statusColor = status => {
  const statusMapping = {
    UNPAID: 'text-gray-400',
    ON_HOLD: 'text-violet-500',
    AWAITING_SHIPMENT: 'text-orange-500',
    PARTIALLY_SHIPPING: 'text-orange-500',
    AWAITING_COLLECTION: 'text-orange-500',
    IN_TRANSIT: 'text-blue-600',
    DELIVERED: 'text-green-500',
    COMPLETED: 'text-green-500',
    CANCELLED: 'text-red-400',
  };

  return statusMapping[status] || 'bg-gray-300';
};

const statusIconCode = computed(() => {
  const statusIcons = {
    unpaid: 'i-lucide-shopping-bag',
    on_hold: 'i-lucide-clock',
    awaiting_shipment: 'i-lucide-package',
    partially_shipping: 'i-lucide-inbox',
    awaiting_collection: 'i-lucide-package-open',
    in_transit: 'i-lucide-truck',
    delivered: 'i-lucide-check-circle',
    completed: 'i-lucide-badge-check',
    cancelled: 'i-ri-alert-line',
  };

  return (
    statusIcons[props.order.status.toLowerCase()] || 'i-lucide-shopping-cart'
  );
});

function onCopyToClipboard(attributeValue) {
  if (!attributeValue) return;
  copyTextToClipboard(attributeValue);
  useAlert(t('GENERAL.COPY_SUCCESSFUL'));
}

const priceFormatter = price => {
  return currencyFormatter(price || 0);
};

const sendOrder = order => {
  store.dispatch('tiktokShop/sendOrderMessage', {
    conversationID: props.currentChat.id,
    orderID: order.orderId,
  });
};
</script>

<template>
  <div class="header flex flex-col text-xs pb-1">
    <div class="flex justify-between items-center">
      <span class="flex-none">
        <Icon
          class="text-lg"
          :class="statusColor(order.status)"
          :icon="statusIconCode"
        />
      </span>
      <span
        class="flex-auto ms-1 font-bold capitalize"
        :class="statusColor(order.status)"
      >
        {{ statusText }}
      </span>
    </div>
    <div
      class="text-xs flex items-center gap-2 border-b border-dashed border-slate-200 dark:border-slate-700"
    >
      <p class="flex justify-between items-center gap-2 w-full pb-1">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.ORDER_ID')
        }}</span>
        <span class="flex items-center gap-2 text-woot-600">
          <span>{{ order.orderId }}</span>
          <button class="p-0" @click="() => onCopyToClipboard(order.orderId)">
            <Icon icon="i-lucide-copy" />
          </button>
        </span>
      </p>
    </div>
    <div
      v-if="order.status == 'CANCELLED'"
      class="text-xs flex flex-col gap-1 border-b border-dashed border-slate-200 dark:border-slate-700 py-2"
    >
      <p class="flex justify-between items-center gap-2">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.CANCELLED_TIME')
        }}</span>
        <span class="text-red-600">{{ order.cancelTime }}</span>
      </p>
      <p class="flex justify-between items-center gap-2">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.CANCELLED_BY')
        }}</span>
        <span class="text-red-600">{{ order.cancelBy }}</span>
      </p>
      <p class="flex justify-between items-center gap-2">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.CANCEL_REASON')
        }}</span>
        <span class="text-red-600">{{ order.cancelReason }}</span>
      </p>
    </div>
    <ul class="list-none">
      <li
        v-for="(item, index) in order.orderItems"
        v-show="toggleExpandOrderIds[order.id] || index < 2"
        :key="item.itemId"
      >
        <div class="flex items-start gap-4 p-4">
          <div
            class="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0"
          >
            <img
              :src="item.imageUrl"
              alt="Item Image"
              class="w-full h-full min-w-full min-h-full object-cover"
            />
          </div>
          <div class="flex flex-col justify-between min-w-0 gap-1">
            <span
              v-tooltip.top="item.itemName"
              class="text-xs font-semibold text-gray-800 line-clamp-2"
            >
              {{ item.itemName }}
            </span>
            <span class="text-xs text-slate-500 dark:text-slate-300">{{
              item.itemSku
            }}</span>
            <span class="text-sm font-medium text-woot-600">{{
              priceFormatter(item.price)
            }}</span>
          </div>
        </div>
      </li>
    </ul>
    <div v-if="order.orderItems.length > 2" class="text-center px-4 pb-2">
      <button class="text-sm text-woot-600" @click="toggle(order.id)">
        {{
          toggleExpandOrderIds[order.id]
            ? t('CONVERSATION.TIKTOKSHOP.ORDER.SHOW_LESS')
            : t('CONVERSATION.TIKTOKSHOP.ORDER.SHOW_MORE')
        }}
      </button>
    </div>
    <div
      class="pt-3 border-dashed border-t border-slate-200 dark:border-slate-700"
    >
      <p class="flex justify-between text-sm">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.ORDERED_AT')
        }}</span>
        <span>{{ order.createdTime }}</span>
      </p>
      <p class="flex justify-between text-sm">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.DELIVERY_OPTION_NAME')
        }}</span>
        <span>{{ order.deliveryOptionName }}</span>
      </p>
      <p v-if="order.cod" class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.PAYMENT_METHOD')
        }}</span>
        <span>{{ t('CONVERSATION.TIKTOKSHOP.ORDER.COD') }}</span>
      </p>
      <p class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.SUB_TOTAL')
        }}</span>
        <span class="font-semibold text-md">{{
          priceFormatter(order.subTotal)
        }}</span>
      </p>
      <p class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.SHIPPING_FEE')
        }}</span>
        <span class="font-semibold text-md">{{
          priceFormatter(order.shippingFee)
        }}</span>
      </p>
      <p class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.TOTAL')
        }}</span>
        <span class="font-semibold text-lg text-woot-600">{{
          priceFormatter(order.totalAmount)
        }}</span>
      </p>
    </div>
    <div class="flex-auto text-end mt-2">
      <button
        class="ms-2 text-white bg-woot-600 rounded-md py-1 px-5 text-sm hover:bg-woot-400 cursor-pointer"
        @click="sendOrder(order)"
      >
        {{ t('CONVERSATION.TIKTOKSHOP.SEND') }}
      </button>
    </div>
  </div>
</template>
